/**
 * @file seo-fields.ts
 * @description Поля общих SEO-настроек магазина (phase19.16, партия Р3в,
 * v0.19.64; SB145, SEO16): подпись поля, поле журнала «было/стало», как
 * показать прежнее значение в списке «Вернуть прежнее», и подпись
 * «изменено <когда> <кем>».
 *
 * Реестр (OCP): новое поле (IndexNow в v0.19.65) — запись в `SEO_FIELDS`,
 * карточки и окно журнала читают реестр, веток по полям в разметке нет.
 */

import type { ShopSeoFieldKey, ShopSeoPending, ShopSeoSettingsDto, ShopSeoValue } from "../api-site-seo";
import { timeAgo } from "../site-builder/page-status";

export interface SeoFieldMeta {
  /** «Окончание заголовков» — в окне «Вернуть прежнее». */
  label: string;
  /** Меняет то, что видно в поиске: после сохранения — «обновится за дни или недели». */
  searchVisible?: true;
  /** Переключатель: значение журнала словами «включено / выключено». */
  onOff?: readonly [on: string, off: string];
}

export const SEO_FIELDS: Readonly<Record<ShopSeoFieldKey, SeoFieldMeta>> = {
  defaultTitle: { label: "Заголовок главной страницы", searchVisible: true },
  titleSuffix: { label: "Окончание заголовков", searchVisible: true },
  description: { label: "Описание по умолчанию", searchVisible: true },
  ogImageMediaId: { label: "Картинка для ссылок" },
  appShortName: { label: "Короткое название для значка" },
  allowIndexing: { label: "Показывать сайт в поиске", onOff: ["показывать в поиске", "закрыт от поисковиков"] },
  yandexVerification: { label: "Коды Яндекс Вебмастера" },
  googleVerification: { label: "Коды Google Search Console" },
  // v0.19.65 (SEO8): IndexNow.
  indexNowEnabled: { label: "Сообщать поисковикам об изменениях", onOff: ["сообщать", "не сообщать"] },
};

/** Подсказка после смены заголовка или описания (SB145). */
export const SEARCH_UPDATE_NOTE = "В поиске обновится за дни или недели — поисковики перечитывают сайт не сразу.";

/** Настройки действуют сразу, витрина берёт их в течение минуты (SB145). */
export const SEO_APPLY_NOTE = "Сохранённое действует на сайте в течение минуты, без публикации.";

/** Значение журнала словами: пусто, «да/нет», список кодов, картинка (id медиа). */
export function historyValueText(value: ShopSeoValue, field: ShopSeoFieldKey | null = null): string {
  if (value === null || value === "") return "пусто";
  if (typeof value === "boolean") {
    const [on, off] = (field && SEO_FIELDS[field].onOff) || ["да", "нет"];
    return value ? on : off;
  }
  if (Array.isArray(value)) return value.length === 0 ? "кодов нет" : value.join(", ");
  if (field === "ogImageMediaId") return "прежняя картинка";
  return value;
}

/** «изменено 5 мин назад · Иван»; не менялось — null. */
export function changedText(mark: ShopSeoSettingsDto["lastChanges"][ShopSeoFieldKey], now: Date = new Date()): string | null {
  if (!mark) return null;
  const who = mark.by ? ` · ${mark.by}` : "";
  return `изменено ${timeAgo(mark.at, now)}${who}`;
}

/** Какие из изменённых полей видны в поиске (для подсказки о сроках). */
export function touchesSearch(keys: readonly ShopSeoFieldKey[]): boolean {
  return keys.some((key) => SEO_FIELDS[key].searchVisible === true);
}

/** «окончание заголовков, описание по умолчанию» — подписи сервера для плашки неопубликованных правок. */
export function pendingFieldsText(pending: ShopSeoPending): string {
  return pending.fields.map((f) => f.label.toLowerCase()).join(", ");
}
